// Brief 93 — Dashboard-launched "Send Source of Funds questionnaire" page.
//
// Brief 95 — renamed "Statement of Funds" → "Source of Funds" throughout and
// added a "Send test copy to Liezel" mode so the questionnaire can be
// rehearsed end-to-end without firing a real client email. The test mode
// posts to the same endpoint with ?test=1 and the backend overrides the
// recipient to SOF_TEST_EMAIL (default: the roster's Executive Assistant) and prefixes
// [TEST] to the email subject.
//
// Thin wrapper around the existing POST /api/sof/send-standalone/{matter_ref}
// endpoint (built in Brief 14e.2). Mirrors the StepMatterSelection pattern
// used by AccountsInstructionPage / CompletionStatementPage etc., so the
// dashboard action card has a natural matter-picker landing surface.
//
// Sends the FULL Phase 1a-Client SoF/SoW questionnaire (not the document
// shopping list — that's the care-pack path which this endpoint deliberately
// avoids per Brief 14e.2 §2).

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../config/api";
import { Button } from "../components/ui/button";
import SmartClientMatterSearch from "../components/SmartClientMatterSearch";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import {
  ArrowLeft,
  Loader2,
  Mail,
  CheckCircle2,
  AlertCircle,
  FlaskConical,
} from "lucide-react";
import { toast } from "sonner";

// ========== STEP 1: Matter Selection ==========
// BM-H4860 (29 Oct 2026): Step 1 used POST /hub/ai-search, which searches
// form submissions (intakes, invoice requests…), not matters, and returns
// rows without matter_reference/client_name/client_email. Open matters such
// as BL.RM.001.1574 never came back, and a row that did would have sent the
// submission id as the matter reference. It now uses the firm-wide open
// matter search (/smart-search, matters only) every other matter picker uses.
function StepMatterSelection({ onSelect }) {
  return (
    <div className="max-w-3xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg text-teal">Step 1 — Select Matter</CardTitle>
          <p className="text-sm text-gray-500">
            Search open matters by reference or client name.
            The client will receive the full structured questionnaire by email.
          </p>
        </CardHeader>
        <CardContent>
          <div data-testid="sof-matter-search">
            <SmartClientMatterSearch
              mattersOnly
              label="Matter"
              placeholder="e.g. 'BL.RM.001.1574' or 'Tommy-Lee Green'"
              onSelect={(r) => {
                // Typing a full reference propagates a "typed" pick on every
                // keystroke; only a row clicked from the list moves to Step 2.
                if (r && r.type !== "typed" && r.matter_reference) onSelect(r);
              }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ========== STEP 2: Confirm & Send ==========
function StepConfirmAndSend({ matter, onBack, onSent }) {
  const [sending, setSending] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sent, setSent] = useState(false);
  const [testSent, setTestSent] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const matterRef = matter?.matter_reference || "";
  // BM-H4860: the search row has no client email, so ask the backend for the
  // exact client and address the send itself will use.
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  useEffect(() => {
    let live = true;
    setLoadingPreview(true);
    const userId = sessionStorage.getItem("bonsai_user_id") || "";
    axios
      .get(`${API}/sof/send-standalone/${encodeURIComponent(matterRef)}/preview`, {
        headers: { "X-Bonsai-User-Id": userId },
      })
      .then((res) => { if (live) setPreview(res.data || {}); })
      .catch(() => { if (live) setPreview({}); })
      .finally(() => { if (live) setLoadingPreview(false); });
    return () => { live = false; };
  }, [matterRef]);
  const clientName = preview?.client_name || matter?.client_name || "—";
  const clientEmail = loadingPreview ? "Loading…" : (preview?.client_email || "—");

  const doSend = async ({ test }) => {
    if (!matterRef) {
      toast.error("Matter reference missing");
      return;
    }
    if (test) setSendingTest(true); else setSending(true);
    setErrorMsg("");
    try {
      const userId = sessionStorage.getItem("bonsai_user_id") || "";
      const qs = test ? "?test=1" : "";
      const res = await fetch(
        `${API}/sof/send-standalone/${encodeURIComponent(matterRef)}${qs}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Bonsai-User-Id": userId,
          },
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Server returned ${res.status}`);
      }
      if (test) {
        setTestSent(true);
        toast.success("Test copy sent to Liezel");
      } else {
        setSent(true);
        toast.success("Source of Funds questionnaire sent");
        if (typeof onSent === "function") onSent();
      }
    } catch (err) {
      const msg = err?.message || (test ? "Failed to send test copy" : "Failed to send Source of Funds questionnaire");
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      if (test) setSendingTest(false); else setSending(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg text-teal">Step 2 — Confirm and Send</CardTitle>
          <p className="text-sm text-gray-500">
            The full Source of Funds questionnaire will be emailed to the client below.
            A request PDF is also filed to the matter SharePoint folder and the send is
            logged to the SoF Sends audit.
          </p>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-gray-200 bg-gray-50 p-4 mb-4 text-sm">
            <dl className="grid grid-cols-3 gap-y-2">
              <dt className="text-gray-500">Matter reference</dt>
              <dd className="col-span-2 font-medium text-gray-900" data-testid="sof-confirm-matter-ref">{matterRef}</dd>
              <dt className="text-gray-500">Client</dt>
              <dd className="col-span-2 text-gray-900" data-testid="sof-confirm-client-name">{clientName}</dd>
              <dt className="text-gray-500">Sending to</dt>
              <dd className="col-span-2 text-gray-900 break-all" data-testid="sof-confirm-client-email">{clientEmail}</dd>
            </dl>
          </div>

          {!loadingPreview && clientEmail === "—" && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 mb-4 text-sm text-amber-900 flex items-start gap-2" data-testid="sof-confirm-missing-email">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>No client email on the matter record. Add one before sending to the client. Test sends are unaffected.</span>
            </div>
          )}

          {errorMsg && (
            <div className="rounded-md border border-status-red/40 bg-status-red-dim/20 px-3 py-2 mb-4 text-sm text-status-red" data-testid="sof-confirm-error">
              {errorMsg}
            </div>
          )}

          {testSent && !sent && (
            <div className="rounded-md border border-indigo-300 bg-indigo-50 px-3 py-2 mb-2 text-sm text-indigo-900 flex items-start gap-2" data-testid="sof-confirm-test-sent">
              <FlaskConical className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Test copy sent to Liezel. The client has not been emailed.</span>
            </div>
          )}

          {sent ? (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 mb-2 text-sm text-emerald-900 flex items-start gap-2" data-testid="sof-confirm-sent">
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Questionnaire sent. The send is recorded in the SoF Sends audit (Directors Lounge).</span>
            </div>
          ) : null}

          <div className="flex items-center justify-between mt-4 gap-2 flex-wrap">
            <Button variant="ghost" onClick={onBack} className="gap-2" data-testid="sof-confirm-back">
              <ArrowLeft className="w-4 h-4" /> Back
            </Button>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={() => doSend({ test: true })}
                disabled={sendingTest || sending}
                variant="outline"
                className="gap-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                data-testid="sof-confirm-test-btn"
              >
                {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
                {sendingTest ? "Sending test…" : "Send test copy to Liezel"}
              </Button>
              <Button
                onClick={() => doSend({ test: false })}
                disabled={sending || sent || loadingPreview || clientEmail === "—"}
                className="bg-teal hover:bg-teal/90 text-white gap-2"
                data-testid="sof-confirm-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {sent ? "Sent" : sending ? "Sending…" : "Send questionnaire"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ========== PAGE ==========
export default function SendSofQuestionnairePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [matter, setMatter] = useState(null);

  return (
    <div className="min-h-screen bg-navy-bg text-white">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate("/")}
              className="text-sm text-txt-muted hover:text-white flex items-center gap-1 mb-2"
              data-testid="sof-page-back-dashboard"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard
            </button>
            <h1 className="text-2xl font-bold">Send Source of Funds questionnaire</h1>
            <p className="text-sm text-txt-muted mt-1">
              Pick a matter, confirm the client email, and send the full SoF/SoW questionnaire.
              Use the test button on Step 2 to send a rehearsal copy to Liezel without emailing the client.
            </p>
          </div>
        </div>

        {step === 1 && (
          <StepMatterSelection
            onSelect={(m) => { setMatter(m); setStep(2); }}
          />
        )}
        {step === 2 && (
          <StepConfirmAndSend
            matter={matter}
            onBack={() => setStep(1)}
            onSent={() => { /* stay on confirm; toast already shown */ }}
          />
        )}
      </div>
    </div>
  );
}
